import { useRef, useEffect } from "react";

// Número do WhatsApp no formato internacional (sem + ou espaços)
const WHATSAPP_NUMBER = "5598992018216";

export default function ContatoSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = "1";
            (entry.target as HTMLElement).style.transform = "translateY(0)";
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll(".reveal-contato");
    elements?.forEach((el) => {
      (el as HTMLElement).style.opacity = "0";
      (el as HTMLElement).style.transform = "translateY(30px)";
      (el as HTMLElement).style.transition = "opacity 0.7s ease, transform 0.7s ease";
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  // Captura os dados do formulário, monta a mensagem e abre o WhatsApp
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // Impede o comportamento padrão do formulário (sem reload, sem fetch)
    e.preventDefault();

    const form = e.currentTarget;

    // Lê os valores dos campos
    const nome = (form.querySelector("input[name='nome']") as HTMLInputElement).value.trim();
    const email = (form.querySelector("input[name='email']") as HTMLInputElement).value.trim();
    const mensagem = (form.querySelector("textarea[name='mensagem']") as HTMLTextAreaElement).value.trim();

    // Monta a mensagem no formato exato solicitado
    const textoMontado = `Nome Completo: ${nome} | E-mail: ${email} | Motivo do Contato: ${mensagem}`;

    // Codifica a mensagem para uso em URL
    const textoCodificado = encodeURIComponent(textoMontado);

    // Monta o link do WhatsApp e abre em nova aba
    const whatsappURL = `https://wa.me/${WHATSAPP_NUMBER}?text=${textoCodificado}`;
    window.open(whatsappURL, "_blank", "noopener,noreferrer");

    // Limpa o formulário após o envio
    form.reset();
  };

  const inputClass = "w-full px-4 py-3.5 rounded-lg text-sm outline-none transition-all duration-200 focus:ring-2 focus:ring-[#0B4D6F]/30";
  const inputStyle = {
    border: "1px solid #E5E5E5",
    fontFamily: "Montserrat, sans-serif",
    color: "#1A1A1A",
    backgroundColor: "#FAFAFA",
  };

  return (
    <section id="contato" ref={sectionRef} className="py-20 md:py-28" style={{ backgroundColor: "#F5F5F5" }}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Cabeçalho da seção */}
        <div className="reveal-contato text-center mb-16">
          <span
            className="inline-block text-sm font-semibold tracking-widest uppercase mb-3"
            style={{ fontFamily: "Montserrat, sans-serif", color: "#0B4D6F" }}
          >
            Fale Conosco
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
            style={{ fontFamily: "Cinzel, serif", color: "#1A1A1A" }}
          >
            Entre em Contato
          </h2>
          <div className="mx-auto" style={{ width: "60px", height: "3px", backgroundColor: "#0B4D6F", borderRadius: "2px" }} />
        </div>

        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">

          {/* Coluna de informações de contato */}
          <div className="reveal-contato flex-1">
            <h3
              className="text-2xl font-semibold mb-6"
              style={{ fontFamily: "Cinzel, serif", color: "#1A1A1A" }}
            >
              Estamos Prontos para Ajudar
            </h3>
            <p
              className="text-base leading-relaxed mb-8"
              style={{ fontFamily: "Montserrat, sans-serif", color: "#555" }}
            >
              Entre em contato agora mesmo. Atendemos pelo WhatsApp, Instagram ou pelo formulário ao lado.
              Resposta rápida e atendimento humanizado.
            </p>

            {/* Links de contato */}
            <div className="flex flex-col gap-4">
              {[
                { icon: "ri-whatsapp-line", label: "WhatsApp", value: "(98) 9 9201-8216", href: "https://wa.me/5598992018216" },
                { icon: "ri-instagram-line", label: "Instagram", value: "@adv.jordannarates", href: "https://instagram.com/adv.jordannarates" },
              ].map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all duration-200 hover:bg-white"
                  style={{ backgroundColor: "#EFEFEF" }}
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: "#0B4D6F" }}
                  >
                    <i className={`${item.icon} text-white text-xl`}></i>
                  </div>
                  <div>
                    <div
                      className="text-xs font-semibold uppercase tracking-wide mb-0.5"
                      style={{ fontFamily: "Montserrat, sans-serif", color: "#888" }}
                    >
                      {item.label}
                    </div>
                    <div
                      className="font-semibold text-sm"
                      style={{ fontFamily: "Montserrat, sans-serif", color: "#1A1A1A" }}
                    >
                      {item.value}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Coluna do formulário */}
          <div className="reveal-contato flex-1">
            <div className="bg-white rounded-2xl p-8 md:p-10" style={{ border: "1px solid #E5E5E5" }}>
              <h3
                className="text-xl font-semibold mb-6"
                style={{ fontFamily: "Cinzel, serif", color: "#1A1A1A" }}
              >
                Envie uma Mensagem
              </h3>

              {/* Formulário — ao enviar abre WhatsApp com mensagem pronta */}
              <form onSubmit={handleSubmit} id="contato-jordanna-rates" noValidate={false}>
                <div className="flex flex-col gap-5">

                  {/* Campo: Nome Completo */}
                  <div>
                    <label
                      className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
                      style={{ fontFamily: "Montserrat, sans-serif", color: "#555" }}
                    >
                      Nome Completo *
                    </label>
                    <input
                      name="nome"
                      type="text"
                      required
                      placeholder="Seu nome completo"
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>

                  {/* Campo: E-mail */}
                  <div>
                    <label
                      className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
                      style={{ fontFamily: "Montserrat, sans-serif", color: "#555" }}
                    >
                      E-mail *
                    </label>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="seu@email.com"
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>

                  {/* Campo: Mensagem */}
                  <div>
                    <label
                      className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
                      style={{ fontFamily: "Montserrat, sans-serif", color: "#555" }}
                    >
                      Mensagem *
                    </label>
                    <textarea
                      name="mensagem"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="Conte brevemente sua situação..."
                      className={`${inputClass} resize-none`}
                      style={inputStyle}
                    />
                    <p
                      className="text-xs mt-1"
                      style={{ fontFamily: "Montserrat, sans-serif", color: "#aaa" }}
                    >
                      Máximo 500 caracteres
                    </p>
                  </div>

                  {/* Botão de envio — abre WhatsApp com mensagem pronta */}
                  <button
                    type="submit"
                    className="w-full py-4 rounded-lg text-white font-semibold text-base cursor-pointer transition-all duration-200 hover:opacity-90 whitespace-nowrap flex items-center justify-center gap-2"
                    style={{ backgroundColor: "#0B4D6F", fontFamily: "Montserrat, sans-serif" }}
                  >
                    <i className="ri-whatsapp-line text-xl"></i>
                    Enviar pelo WhatsApp
                  </button>

                  <p
                    className="text-xs text-center"
                    style={{ fontFamily: "Montserrat, sans-serif", color: "#aaa" }}
                  >
                    Ao clicar, você será direcionado ao WhatsApp com sua mensagem já preenchida.
                  </p>
                </div>
              </form>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
